
import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ArrowLeft, Edit, Eye, UserPlus } from 'lucide-react';
import { Assessment, AssessmentResponse } from '@/types/assessment';
import { getAssessmentById } from '@/services/assessment';
import { getAssessmentResponses } from '@/services/responseService';
import { useToast } from '@/hooks/use-toast';
import { OverviewTab } from '@/components/admin/assessment/OverviewTab';
import { ResponsesTab } from '@/components/admin/assessment/ResponsesTab';
import { InviteToAssessmentDialog } from '@/components/admin/assessment/InviteToAssessmentDialog';
import { useAuth } from '@/contexts/AuthContext';

export default function AssessmentView() {
  const { id } = useParams<{ id: string }>();
  const [assessment, setAssessment] = useState<Assessment | null>(null);
  const [responses, setResponses] = useState<AssessmentResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const { toast } = useToast();
  const { isPlatformAdmin } = useAuth();
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!id) return;
        
        const assessmentData = await getAssessmentById(id);
        setAssessment(assessmentData);
        
        if (assessmentData) {
          const responseData = await getAssessmentResponses(id);
          setResponses(responseData);
        }
      } catch (error) {
        console.error("Error fetching assessment:", error);
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to load assessment details."
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [id, toast]);

  const handleUserInvited = () => {
    // Refresh responses or show success message
    toast({
      title: "Success",
      description: "Assessment invitation sent successfully!"
    });
  };
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-primary">Loading assessment details...</div>
      </div>
    );
  }
  
  if (!assessment) {
    return (
      <div className="text-center p-10">
        <h3 className="text-lg font-medium">Assessment not found</h3>
        <p className="text-muted-foreground mt-2">
          The assessment you're looking for doesn't exist or has been removed.
        </p>
        <Button asChild className="mt-4">
          <Link to="/admin/assessments">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Assessments
          </Link>
        </Button>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <Button variant="ghost" asChild>
            <Link to="/admin/assessments">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Link>
          </Button>
          <div>
            <h1 className="text-lg font-semibold mb-1">{assessment.title}</h1>
            <p className="text-sm text-muted-foreground">
              Version {assessment.version} · 
              <span className={`ml-2 ${assessment.isActive ? 'text-green-600' : 'text-gray-500'}`}>
                {assessment.isActive ? 'Active' : 'Archived'}
              </span>
            </p>
          </div>
        </div>
        
        <div className="flex items-center space-x-2">
          {isPlatformAdmin && (
            <Button 
              variant="outline" 
              onClick={() => setInviteDialogOpen(true)}
            >
              <UserPlus className="mr-2 h-4 w-4" />
              Invite Users
            </Button>
          )}
          <Button variant="outline" asChild>
            <Link to={`/assessment/${assessment.slug}`} target="_blank">
              <Eye className="mr-2 h-4 w-4" />
              Preview
            </Link>
          </Button>
          <Button asChild>
            <Link to={`/admin/assessments/${assessment.id}/edit`}>
              <Edit className="mr-2 h-4 w-4" />
              Edit Assessment
            </Link>
          </Button>
        </div>
      </div>
      
      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="responses">Responses</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-6 mt-6">
          <OverviewTab assessment={assessment} responsesCount={responses.length} />
        </TabsContent>
        
        <TabsContent value="responses" className="space-y-6 mt-6">
          <ResponsesTab assessment={assessment} responses={responses} />
        </TabsContent>
      </Tabs>

      {isPlatformAdmin && assessment && (
        <InviteToAssessmentDialog
          open={inviteDialogOpen}
          onOpenChange={setInviteDialogOpen}
          onUserInvited={handleUserInvited}
          assessment={assessment}
        />
      )}
    </div>
  );
}
